// Role indicator component that shows user's roles and permissions
import { useState } from 'react'
import { Shield, Info, Check, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useUser, usePermissions } from '@/lib/hooks'
import { useProject } from '@/contexts/project-context'

export default function RoleIndicator() {
  const [showModal, setShowModal] = useState(false)
  const { profile } = useUser()
  const { currentProject, projectMembers } = useProject()
  const permissions = usePermissions()

  // Get user's global role
  const globalRole = (profile?.roles as any)?.name || 'employee'
  
  // Get user's project role if in a project
  const projectRole = currentProject && profile?.id 
    ? projectMembers.find(member => member.user_id === profile.id && member.project_id === currentProject.id)?.role
    : null

  // Format role display
  const formatRole = (role: string) => {
    return role.charAt(0).toUpperCase() + role.slice(1)
  }

  // Get role badge variant
  const getRoleVariant = (role: string) => {
    switch (role) {
      case 'founder':
      case 'admin':
      case 'owner':
        return 'default' as const
      case 'investor':
      case 'advisor':
        return 'secondary' as const
      default:
        return 'outline' as const
    }
  }

  const permissionsList = [
    {
      category: 'Global Permissions',
      permissions: [
        { name: 'Invite Users', allowed: permissions.canInviteUsers },
        { name: 'Manage All Projects', allowed: permissions.canManageAllProjects },
        { name: 'View All Projects', allowed: permissions.canViewAllProjects },
        { name: 'Create Equity Offers', allowed: permissions.canMakeEquityOffers },
      ]
    },
    ...(currentProject ? [{
      category: `Project Permissions (${currentProject.name})`,
      permissions: [
        { name: 'Edit Project', allowed: permissions.canEditProject },
        { name: 'Delete Project', allowed: permissions.canDeleteProject },
        { name: 'Manage Project Members', allowed: permissions.canManageProjectMembers },
        { name: 'Assign Tasks', allowed: permissions.canAssignTasks },
        { name: 'Edit Tasks', allowed: permissions.canEditTasks },
        { name: 'View Tasks', allowed: permissions.canViewTasks },
        { name: 'Create Contributions', allowed: permissions.canCreateContributions },
      ]
    }] : [])
  ]

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors"
      >
        <Shield className="mr-3 h-5 w-5" />
        <div className="flex-1 text-left">
          <div className="flex items-center gap-2">
            <Badge variant={getRoleVariant(globalRole)}>
              {formatRole(globalRole)}
            </Badge>
            {projectRole && (
              <Badge variant={getRoleVariant(projectRole)} className="text-xs">
                {formatRole(projectRole)}
              </Badge>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {permissions.isViewOnly ? 'View-only access' : 'Click to see permissions'}
          </div>
        </div>
        <Info className="h-4 w-4 ml-2 text-gray-400" />
      </button>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Your Roles & Permissions
            </DialogTitle>
            <DialogDescription>
              Here's what you can do with your current roles in the system.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Roles Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Active Roles</h4>
              <div className="flex flex-wrap gap-2">
                <Badge variant={getRoleVariant(globalRole)}>
                  {formatRole(globalRole)} (Global)
                </Badge>
                {projectRole && currentProject && (
                  <Badge variant={getRoleVariant(projectRole)}>
                    {formatRole(projectRole)} ({currentProject.name})
                  </Badge>
                )}
              </div>
              {permissions.isViewOnly && (
                <p className="text-xs text-gray-600 mt-2">
                  ⚠️ You have view-only access to most features
                </p>
              )}
            </div>

            {/* Permissions List */}
            <div className="space-y-3">
              {permissionsList.map((section) => (
                <div key={section.category}>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    {section.category}
                  </h4>
                  <div className="space-y-1">
                    {section.permissions.map((permission) => (
                      <div
                        key={permission.name}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className={permission.allowed ? 'text-gray-900' : 'text-gray-500'}>
                          {permission.name}
                        </span>
                        {permission.allowed ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <X className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Help Text */}
            <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-800">
              <p>
                <strong>Need different permissions?</strong> Contact your project admin or company founder to request role changes.
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
} 